import React, { useState, useEffect, useRef, useCallback } from 'react';

const MusicPlayer = ({ isMinimized }) => {
  // Default playlist with actual music files in the public assets folder
  const defaultPlaylist = [
    {
      id: 1,
      title: "Die With A Smile",
      artist: "SambalpuriStar",
      source: `/assets/music/Die With A Smile-(SambalpuriStar.In).mp3`,
      cover: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?q=80&w=400&auto=format&fit=crop",
      duration: "3:42"
    },
    {
      id: 2,
      title: "Apt",
      artist: "PagalHits",
      source: `/assets/music/Apt - PagalHits.mp3`,
      cover: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?q=80&w=400&auto=format&fit=crop",
      duration: "4:19"
    },
    {
      id: 3,
      title: "The Weeknd - São Paulo",
      artist: "HipHopKit",
      source: `/assets/music/The-Weeknd-S-o-Paulo-(HipHopKit.com).mp3`,
      cover: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?q=80&w=400&auto=format&fit=crop",
      duration: "3:05"
    }
  ];

  // Test utility function to check if audio files exist
  const testAudioFiles = () => {
    defaultPlaylist.forEach(song => {
      const testAudio = new Audio();
      const absolutePath = `${window.location.origin}${song.source}`;
      testAudio.src = absolutePath;
      
      console.log(`Testing audio file: ${absolutePath}`);
      
      // Listen for error events
      testAudio.addEventListener('error', (e) => {
        console.error(`ERROR loading audio file: ${absolutePath}`, e);
      });
      
      // Listen for success events
      testAudio.addEventListener('canplaythrough', () => {
        console.log(`SUCCESS: Can play audio file: ${absolutePath}`);
      });
      
      testAudio.load();
    });
  };
  
  // Run the test on component mount
  useEffect(() => {
    testAudioFiles();
  }, []);

  // Replace initial playlist state
  const [playlist, setPlaylist] = useState(() => {
    try {
      const savedPlaylist = localStorage.getItem('musicPlayerPlaylist');
      return savedPlaylist ? JSON.parse(savedPlaylist) : defaultPlaylist;
    } catch (error) {
      console.error('Error loading playlist:', error);
      return defaultPlaylist;
    }
  });
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [visualizerData, setVisualizerData] = useState(Array(64).fill(0));
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [theme, setTheme] = useState('dark');
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [draggedSong, setDraggedSong] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Refs
  const audioRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Add a reference for audio context to avoid recreation issues
  const audioContextCreatedRef = useRef(false);
  
  // Clear any old state on component mount to use the new songs
  useEffect(() => {
    // Clear previous storage data to use the new songs
    localStorage.removeItem('musicPlayerState');
    localStorage.removeItem('musicPlayerPlaylist');
    localStorage.removeItem('webos-music-player');
    
    // Set initial song
    if (playlist.length > 0) {
      setCurrentSong(playlist[0]);
      setCurrentSongIndex(0);
    }
  }, []);
  
  // Load saved state on component mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('webos-music-player');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (parsedState.volume) setVolume(parsedState.volume);
        if (parsedState.currentSongId) {
          const savedSong = playlist.find(song => song.id === parsedState.currentSongId);
          if (savedSong) setCurrentSong(savedSong);
        } else if (playlist.length > 0) {
          setCurrentSong(playlist[0]);
        }
        if (parsedState.isRepeat !== undefined) setIsRepeat(parsedState.isRepeat);
        if (parsedState.isShuffle !== undefined) setIsShuffle(parsedState.isShuffle);
        if (parsedState.showPlaylist !== undefined) setShowPlaylist(parsedState.showPlaylist);
      } else if (playlist.length > 0) {
        setCurrentSong(playlist[0]);
      }
    } catch (error) {
      console.error("Error loading music player state:", error);
      if (playlist.length > 0) {
        setCurrentSong(playlist[0]);
      }
    }
    
    // Initialize audio context for visualizer
    if (typeof window !== 'undefined') {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 128;
      }
    }
    
    return () => {
      // Cleanup animation and audio context
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  // Save state when important values change
  useEffect(() => {
    if (currentSong || volume !== 0.7 || isRepeat || isShuffle) {
      const stateToSave = {
        currentSongId: currentSong?.id,
        volume,
        isRepeat,
        isShuffle,
        showPlaylist
      };
      localStorage.setItem('webos-music-player', JSON.stringify(stateToSave));
    }
  }, [currentSong, volume, isRepeat, isShuffle, showPlaylist]);
  
  // Completely revamp audio element setup - this is the core function to fix audio issues
  useEffect(() => {
    if (!currentSong) return;
    
    console.log('Attempting to load and play song:', currentSong.title);
    console.log('Full song source path:', currentSong.source);
    
    // Make sure we have a valid audio element
    if (!audioRef.current) {
      console.error('Audio element reference is not available');
      return;
    }
    
    // Pause any current playback and reset
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    
    // Clean up any previous audio context to avoid multiple contexts
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (e) {
        console.log('Error disconnecting previous source:', e);
      }
    }
    
    // Always set the cross-origin attribute
    audioRef.current.crossOrigin = "anonymous";
    
    // Set the new source with the absolute URL
    audioRef.current.src = currentSong.source;
    
    // Force the audio element to reload the source
    audioRef.current.load();
    
    // Set up the audio context once
    if (!audioContextCreatedRef.current && window.AudioContext) {
      try {
        // Create new AudioContext if it doesn't exist
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        // Create new analyzer
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 128;
        
        // Connect the audio element to the analyzer
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
        
        audioContextCreatedRef.current = true;
        console.log('Audio context and analyzer setup successful');
      } catch (e) {
        console.error('Failed to setup audio context:', e);
      }
    }
    
    // Add more detailed event listeners for debugging
    const onLoadStart = () => console.log('Audio load started for:', currentSong.title);
    const onCanPlay = () => console.log('Audio can play now:', currentSong.title);
    const onPlaying = () => console.log('Audio is now playing:', currentSong.title);
    
    audioRef.current.addEventListener('loadstart', onLoadStart);
    audioRef.current.addEventListener('canplay', onCanPlay);
    audioRef.current.addEventListener('playing', onPlaying);
    
    // If playback is requested, try to play with retry mechanism
    if (isPlaying) {
      const attemptPlay = () => {
        // IMPORTANT: Check for user interaction requirements
        console.log('Attempting to play audio...');
        console.log('Audio element readyState:', audioRef.current.readyState);
        
        // Resume AudioContext if it's suspended (required by some browsers)
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().catch(e => {
            console.error('Failed to resume audio context:', e);
          });
        }
        
        // Attempt to play with proper error handling
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log('Audio playback started successfully!');
            // Start the visualizer animation once playback begins
            if (analyserRef.current) {
              console.log('Starting visualizer animation');
              updateVisualizer();
            }
          }).catch(error => {
            console.error('Error during playback:', error);
            
            // Handle autoplay restrictions
            if (error.name === 'NotAllowedError') {
              console.log('Autoplay prevented. Please interact with the page first.');
              setIsPlaying(false);
            } else {
              // Try again with a longer delay for other errors
              console.log('Retrying playback after delay...');
              setTimeout(attemptPlay, 1000);
            }
          });
        }
      };
      
      // Use a delay before playing to ensure the audio has time to load
      setTimeout(attemptPlay, 300);
    }
    
    // Cleanup on unmount or when song changes
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('loadstart', onLoadStart);
        audioRef.current.removeEventListener('canplay', onCanPlay);
        audioRef.current.removeEventListener('playing', onPlaying);
      }
      
      // Cancel any visualizer animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentSong]);
  
  // Update time display while playing
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);
  
  // Handle autoplay when current song changes
  useEffect(() => {
    if (currentSong && isPlaying) {
      audioRef.current.play().catch(error => {
        console.error("Error playing audio:", error);
        setIsPlaying(false);
      });
    }
  }, [currentSong]);
  
  // Visualizer animation function
  const updateVisualizer = () => {
    cancelAnimationFrame(animationFrameRef.current);
    
    if (!analyserRef.current || !isPlaying || !audioRef.current) {
      // Reset visualizer data when not playing
      setVisualizerData(Array(64).fill(0));
      return;
    }
    
    try {
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Normalize values for visualization
      const normalizedData = Array.from(dataArray).map(value => value / 255);
      setVisualizerData(normalizedData);
      
      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(updateVisualizer);
    } catch (error) {
      console.error('Error updating visualizer:', error);
      // Don't completely stop on error, retry after a delay
      animationFrameRef.current = setTimeout(() => {
        animationFrameRef.current = requestAnimationFrame(updateVisualizer);
      }, 1000);
    }
  };
  
  // Function to create a troubleshooting message
  const getTroubleshootingMessage = () => {
    if (!currentSong) return "Select a song to play";
    if (!isPlaying) return "Press play to start";
    
    if (audioRef.current) {
      const readyState = audioRef.current.readyState;
      const networkState = audioRef.current.networkState;
      
      if (networkState === 3) return "Error loading audio file";
      if (readyState === 0) return "Loading audio...";
      if (readyState < 3) return "Buffering audio...";
    }
    
    return isPlaying ? "Playing..." : "Paused";
  };
  
  // Play/pause toggle
  const togglePlay = useCallback(() => {
    if (!audioRef.current || !currentSong) {
      console.log('Cannot play - no audio ref or no song selected');
      return;
    }

    console.log('Toggle play called, current state:', isPlaying ? 'playing → pausing' : 'paused → playing');
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Use the play promise to handle autoplay restrictions
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Playback started successfully');
            setIsPlaying(true);
          })
          .catch(error => {
            console.error('Playback failed:', error);
            // Auto-recovery attempt
            if (error.name === 'NotAllowedError') {
              console.log('Playback blocked by browser. Waiting for user interaction...');
              // Keep UI in non-playing state until user interacts
              setIsPlaying(false);
            } else {
              // For other errors, try once more after a short delay
              setTimeout(() => {
                console.log('Attempting playback again after error...');
                audioRef.current?.play()
                  .then(() => setIsPlaying(true))
                  .catch(e => {
                    console.error('Second play attempt also failed:', e);
                    setIsPlaying(false);
                  });
              }, 1000);
            }
          });
      } else {
        // Older browsers might not return a promise
        setIsPlaying(true);
      }
    }
  }, [audioRef.current, currentSong, isPlaying]);
  
  // Skip to next song
  const nextSong = () => {
    if (playlist.length === 0) return;
    
    let newIndex;
    if (isShuffle) {
      // Random song selection
      newIndex = Math.floor(Math.random() * playlist.length);
    } else {
      // Next song in order
      newIndex = (currentSongIndex + 1) % playlist.length;
    }
    
    const nextSong = playlist[newIndex];
    
    setCurrentSongIndex(newIndex);
    setCurrentSong(nextSong);
    
    // Reset time
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };
  
  // Skip to previous song
  const prevSong = () => {
    if (playlist.length === 0) return;
    
    // If more than 3 seconds into the song, restart it
    if (currentTime > 3 && audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    
    let newIndex;
    if (isShuffle) {
      // Random song selection
      newIndex = Math.floor(Math.random() * playlist.length);
    } else {
      // Previous song in order
      newIndex = (currentSongIndex - 1 + playlist.length) % playlist.length;
    }
    
    const prevSong = playlist[newIndex];
    
    setCurrentSongIndex(newIndex);
    setCurrentSong(prevSong);
    
    // Reset time
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };
  
  // Seek to position in song
  const seekTo = (e) => {
    if (!audioRef.current || !duration) return;
    
    const seekTime = (e.nativeEvent.offsetX / e.target.clientWidth) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };
  
  // Change volume
  const changeVolume = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    } else if (newVolume === 0 && !isMuted) {
      setIsMuted(true);
    }
  };
  
  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      if (volume > 0) {
        setVolume(0);
        audioRef.current.volume = 0;
      } else {
        setVolume(0.7);
        audioRef.current.volume = 0.7;
      }
      setIsMuted(!isMuted);
    }
  };
  
  // Format time in seconds to mm:ss
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };
  
  // Handle song ending
  const handleTrackEnd = () => {
    if (!audioRef.current) return;
    
    if (isRepeat) {
      // Replay the current song
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(error => {
        console.error("Error playing audio:", error);
        setIsPlaying(false);
      });
    } else if (isShuffle) {
      // Pick a random song from the playlist
      const randomIndex = Math.floor(Math.random() * playlist.length);
      setCurrentSongIndex(randomIndex);
      setCurrentSong(playlist[randomIndex]);
    } else {
      // Play next song if available
      nextSong();
    }
  };
  
  // Handle audio metadata loaded
  const handleMetadataLoaded = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      
      // Set up audio context for visualization, if not already set up
      if (!analyserRef.current && window.AudioContext) {
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 128;
          
          const source = audioContext.createMediaElementSource(audioRef.current);
          source.connect(analyser);
          analyser.connect(audioContext.destination);
          
          analyserRef.current = analyser;
          sourceRef.current = source;
          
          // Start visualization
          updateVisualizer();
        } catch (error) {
          console.error('Error setting up audio visualization:', error);
        }
      }
    }
  };
  
  // Select a song from the playlist
  const selectSong = (song) => {
    setCurrentSong(song);
    
    // Find the index of the song in the playlist
    const index = playlist.findIndex(item => item.id === song.id);
    if (index !== -1) {
      setCurrentSongIndex(index);
    }
    
    // Don't start playing until the audio element is ready
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(error => {
            console.error("Error playing selected song:", error);
            setIsPlaying(false);
          });
      }
    }, 100);
  };
  
  // Toggle playlist visibility
  const togglePlaylist = () => {
    setShowPlaylist(!showPlaylist);
  };
  
  // Toggle repeat mode
  const toggleRepeat = () => {
    setIsRepeat(!isRepeat);
  };
  
  // Toggle shuffle mode
  const toggleShuffle = () => {
    setIsShuffle(!isShuffle);
  };
  
  // Get volume icon based on current volume
  const getVolumeIcon = () => {
    if (volume === 0) return 'fas fa-volume-mute';
    if (volume < 0.3) return 'fas fa-volume-off';
    if (volume < 0.7) return 'fas fa-volume-down';
    return 'fas fa-volume-up';
  };
  
  // Handle playlist reordering
  const handlePlaylistReorder = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const updatedPlaylist = [...playlist];
    const [movedItem] = updatedPlaylist.splice(fromIndex, 1);
    updatedPlaylist.splice(toIndex, 0, movedItem);
    
    setPlaylist(updatedPlaylist);
    
    // Update current song index if needed
    if (currentSongIndex === fromIndex) {
      setCurrentSongIndex(toIndex);
    } else if (
      (fromIndex < currentSongIndex && toIndex >= currentSongIndex) ||
      (fromIndex > currentSongIndex && toIndex <= currentSongIndex)
    ) {
      setCurrentSongIndex(prev => 
        fromIndex < prev ? prev - 1 : prev + 1
      );
    }
  }, [playlist, currentSongIndex]);
  
  // Mini Player component
  const MiniPlayer = () => {
    // Effect to find the current app ID for the music player
    const [musicPlayerAppId, setMusicPlayerAppId] = useState(null);
    
    useEffect(() => {
      // Function to find the MusicPlayer app in the open apps list
      const findMusicPlayerApp = () => {
        const appElements = document.querySelectorAll('.window');
        for (const element of appElements) {
          if (element.innerHTML.includes('MusicPlayer')) {
            // Try to extract the app ID from the element's ID or data attributes
            const appId = element.id || element.getAttribute('data-app-id');
            if (appId) {
              console.log('Found MusicPlayer app ID:', appId);
              setMusicPlayerAppId(appId);
              return;
            }
          }
        }
        // Fallback method - try to catch the ID from app instances
        const matches = document.body.innerHTML.match(/MusicPlayer[^"']*-(\d+)/);
        if (matches && matches[0]) {
          console.log('Found MusicPlayer app ID using regex:', matches[0]);
          setMusicPlayerAppId(matches[0]);
        }
      };
      
      findMusicPlayerApp();
    }, []);
    
    return (
      <div style={{
        position: 'fixed',
        bottom: '50px',
        right: '10px',
        width: '300px',
        height: '70px',
        backgroundColor: 'rgba(20, 20, 40, 0.8)',
        backdropFilter: 'blur(10px)',
        borderRadius: '10px',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        zIndex: 1000,
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Album art */}
        <div style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          overflow: 'hidden',
          marginRight: '10px',
          flexShrink: 0,
          position: 'relative'
        }}>
          {currentSong ? (
            <>
              <img 
                src={currentSong.cover} 
                alt={currentSong.title}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  transform: isPlaying ? 'scale(1.05)' : 'scale(1)'
                }}
              />
              {/* Small visualizer overlay */}
              {isPlaying && (
                <div style={{
                  position: 'absolute',
                  bottom: '5px',
                  left: '5px',
                  right: '5px',
                  height: '15px',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between'
                }}>
                  {[...Array(5)].map((_, i) => {
                    const value = visualizerData[i * 10] || 0;
                    return (
                      <div 
                        key={i}
                        style={{
                          width: '3px',
                          height: `${value * 15}px`,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '1px'
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(30, 30, 50, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-music" style={{ fontSize: '20px', opacity: 0.5 }}></i>
            </div>
          )}
        </div>
        
        {/* Song info and controls */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: '14px', 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis'
          }}>
            {currentSong?.title || 'No song selected'}
          </div>
          <div style={{ 
            fontSize: '12px', 
            opacity: 0.7,
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            marginBottom: '5px'
          }}>
            {currentSong?.artist || 'Select a song to play'}
          </div>
          
          {/* Progress and time */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {/* Time indicator */}
            <span style={{ fontSize: '10px', opacity: 0.7, width: '30px' }}>
              {formatTime(currentTime)}
            </span>
            
            {/* Progress bar */}
            <div style={{ 
              flex: 1,
              height: '3px', 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '1.5px',
              position: 'relative',
              cursor: 'pointer'
            }} 
            onClick={(e) => {
              const seekTime = (e.nativeEvent.offsetX / e.target.clientWidth) * duration;
              if (audioRef.current) {
                audioRef.current.currentTime = seekTime;
                setCurrentTime(seekTime);
              }
            }}>
              <div style={{ 
                height: '100%', 
                width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                backgroundColor: 'rgba(74, 144, 226, 0.8)',
                borderRadius: '1.5px',
                transition: 'width 0.1s linear'
              }}></div>
            </div>
            
            {/* Volume indicator */}
            <button
              onClick={toggleMute}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '10px',
                cursor: 'pointer',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                width: '20px',
                justifyContent: 'center'
              }}
            >
              <i className={getVolumeIcon()}></i>
            </button>
          </div>
        </div>
        
        {/* Controls */}
        <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
          <button 
            onClick={prevSong}
            style={{ 
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '5px',
              opacity: 0.8,
              borderRadius: '50%'
            }}
          >
            <i className="fas fa-step-backward"></i>
          </button>
          
          <button 
            onClick={togglePlay}
            style={{ 
              backgroundColor: 'rgba(74, 144, 226, 0.5)',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className={isPlaying ? 'fas fa-pause' : 'fas fa-play'} style={{ marginLeft: isPlaying ? 0 : '2px' }}></i>
          </button>
          
          <button 
            onClick={nextSong}
            style={{ 
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '5px',
              opacity: 0.8,
              borderRadius: '50%'
            }}
          >
            <i className="fas fa-step-forward"></i>
          </button>
        </div>
        
        {/* Add the maximize button to the mini player */}
        <button
          onClick={() => {
            // Log the event being dispatched
            console.log('Dispatching maximize event, appId:', musicPlayerAppId || 'music-player');
            
            // Create event to maximize the window - use the found ID if possible
            const toggleEvent = new CustomEvent('webos-toggle-window', { 
              detail: { appId: musicPlayerAppId || 'music' }
            });
            window.dispatchEvent(toggleEvent);
            
            // Also try directly triggering click on the taskbar item as a fallback
            const musicTaskbarItem = document.querySelector('[data-component="MusicPlayer"]');
            if (musicTaskbarItem) {
              console.log('Found Music Player taskbar item, clicking it');
              musicTaskbarItem.click();
            }
          }}
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            backgroundColor: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '10px',
            cursor: 'pointer',
            padding: '3px'
          }}
        >
          <i className="fas fa-expand"></i>
        </button>
      </div>
    );
  };

  const savePlayerState = () => {
    try {
      const currentPlaybackState = {
        currentSongIndex,
        currentTime: audioRef.current ? audioRef.current.currentTime : 0,
        volume,
        isPlaying,
        isShuffle,
        isRepeat,
        playlist
      };
      localStorage.setItem('musicPlayerState', JSON.stringify(currentPlaybackState));
    } catch (error) {
      console.error('Error saving music player state:', error);
    }
  };

  const loadPlayerState = () => {
    try {
      const savedState = localStorage.getItem('musicPlayerState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        setCurrentSongIndex(parsedState.currentSongIndex);
        setCurrentSong(parsedState.playlist[parsedState.currentSongIndex]);
        setVolume(parsedState.volume);
        setIsShuffle(parsedState.isShuffle);
        setIsRepeat(parsedState.isRepeat);
        
        // We'll set the current time after the audio element is loaded
        if (audioRef.current) {
          setTimeout(() => {
            audioRef.current.currentTime = parsedState.currentTime;
            audioRef.current.volume = parsedState.volume;
            if (parsedState.isPlaying) {
              audioRef.current.play().catch(err => console.error('Error auto-playing:', err));
            }
          }, 300);
        }
      }
    } catch (error) {
      console.error('Error loading music player state:', error);
    }
  };

  // Load state on mount
  useEffect(() => {
    loadPlayerState();
  }, []);

  // Save state on change and before unmount
  useEffect(() => {
    const saveInterval = setInterval(savePlayerState, 5000);
    
    window.addEventListener('beforeunload', savePlayerState);
    
    return () => {
      clearInterval(saveInterval);
      savePlayerState(); // Save on unmount
      window.removeEventListener('beforeunload', savePlayerState);
    };
  }, [currentSongIndex, currentTime, volume, isPlaying, isShuffle, isRepeat, playlist]);

  // Initialize with first song if available
  useEffect(() => {
    if (!currentSong && playlist.length > 0) {
      setCurrentSong(playlist[0]);
      setCurrentSongIndex(0);
    }
  }, [playlist, currentSong]);

  // Update audio element when isPlaying state changes
  useEffect(() => {
    if (!audioRef.current || !currentSong) return;
    
    if (isPlaying) {
      console.log('Play state changed to playing, attempting to play if needed');
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.error('Error when play state changed:', err);
          // Don't update state here to avoid loop
        });
      }
    } else {
      console.log('Play state changed to paused, pausing playback');
      audioRef.current.pause();
    }
  }, [isPlaying, currentSong]);
  
  // Update audio volume when volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      console.log('Updated volume to:', volume);
    }
  }, [volume]);

  // Create a click handler to enable audio
  const enableAudio = () => {
    // This helps bypass autoplay restrictions by using a user interaction
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().then(() => {
        console.log('AudioContext resumed successfully after user interaction');
      }).catch(err => {
        console.error('Failed to resume AudioContext:', err);
      });
    }
    
    // Try playing if a song is selected
    if (currentSong && audioRef.current && !isPlaying) {
      setIsPlaying(true);
    }
  };

  // Add a button overlay to fix autoplay issues
  const AudioEnableOverlay = () => (
    <div 
      onClick={enableAudio}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 100,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: '20px 30px',
        borderRadius: '10px',
        textAlign: 'center',
        cursor: 'pointer',
        display: audioContextRef.current?.state === 'suspended' ? 'block' : 'none'
      }}
    >
      <div style={{ fontSize: '50px', marginBottom: '10px' }}>
        <i className="fas fa-music"></i>
      </div>
      <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
        Click to Enable Audio
      </div>
      <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8 }}>
        Browser requires user interaction
      </div>
    </div>
  );

  // Render the appropriate player based on minimized state
  if (isMinimized) {
    return <MiniPlayer />;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      backgroundColor: 'rgba(10, 10, 20, 0.2)',
      backdropFilter: 'blur(10px)',
      color: 'white',
      boxSizing: 'border-box'
    }}>
      {/* Audio Element (invisible) */}
      <audio 
        ref={audioRef}
        src={currentSong?.source || ''}
        onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
        onLoadedMetadata={handleMetadataLoaded}
        onEnded={handleTrackEnd}
        onError={(e) => {
          console.error("Audio error:", e);
          console.log("Current source:", audioRef.current?.src);
          
          // Try reloading after a short delay
          setTimeout(() => {
            if (audioRef.current && currentSong) {
              console.log("Attempting to reload audio...");
              audioRef.current.src = currentSong.source;
              audioRef.current.load();
            }
          }, 1000);
        }}
        preload="auto"
        crossOrigin="anonymous"
        controls={false}
      />

      {/* Audio Enable Overlay */}
      <AudioEnableOverlay />

      {/* Debug status message - useful for troubleshooting */}
      <div style={{ 
        position: 'fixed', 
        bottom: '10px', 
        left: '50%', 
        transform: 'translateX(-50%)',
        padding: '8px 12px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: '#fff',
        borderRadius: '4px',
        fontSize: '12px',
        zIndex: 9999,
        pointerEvents: 'none',
        transition: 'opacity 0.3s ease'
      }}>
        Audio State: {audioRef.current?.readyState || 'undefined'} | 
        Volume: {volume.toFixed(2)} |
        Context: {audioContextRef.current?.state || 'none'} |
        {getTroubleshootingMessage()}
      </div>
      
      <div className="music-player-container" style={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Left side - Playlist */}
        <div className="playlist-container" style={{
          width: showPlaylist ? '30%' : '0',
          height: '100%',
          transition: 'width 0.3s ease',
          overflow: 'hidden',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(20, 20, 40, 0.3)',
        }}>
          <div style={{ padding: '16px' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '1.5rem' }}>Your Playlist</h2>
            <div className="playlist-songs" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 200px)'
            }}>
              {playlist.map((song, index) => (
                <div 
                  key={index}
                  draggable
                  onDragStart={(e) => setDraggedSong(index)}
                  onDragEnd={() => {
                    if (draggedSong !== null && dragOverIndex !== null) {
                      handlePlaylistReorder(draggedSong, dragOverIndex);
                    }
                    setDraggedSong(null);
                    setDragOverIndex(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (draggedSong !== index) {
                      setDragOverIndex(index);
                    }
                  }}
                  style={{ 
                    padding: '8px 10px',
                    margin: '5px 0',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: currentSong && currentSong.title === song.title ? 
                      'rgba(74, 144, 226, 0.3)' : 
                      dragOverIndex === index ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: dragOverIndex === index ? 
                      '1px dashed rgba(255, 255, 255, 0.5)' : 
                      '1px solid transparent',
                    opacity: draggedSong === index ? 0.5 : 1
                  }}
                  onClick={() => {
                    setCurrentSong(song);
                    setCurrentSongIndex(index);
                    if (audioRef.current) {
                      // Reset current time
                      audioRef.current.currentTime = 0;
                      setCurrentTime(0);
                      
                      // Set source and load audio
                      audioRef.current.src = song.source;
                      audioRef.current.load();
                      
                      // Pause current playback
                      audioRef.current.pause();
                      
                      // Set playing state and audio will play via the isPlaying effect
                      setIsPlaying(true);
                    }
                  }}
                >
                  <div style={{ 
                    width: '30px', 
                    height: '30px', 
                    borderRadius: '4px', 
                    overflow: 'hidden',
                    marginRight: '10px',
                    flexShrink: 0,
                    position: 'relative'
                  }}>
                    <img 
                      src={song.cover} 
                      alt={song.title}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }}
                    />
                    {currentSong && currentSong.title === song.title && isPlaying && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fas fa-play" style={{ fontSize: '12px' }}></i>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ 
                      fontWeight: currentSong && currentSong.title === song.title ? 'bold' : 'normal', 
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {song.title}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      opacity: 0.7,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {song.artist}
                    </div>
                  </div>
                  
                  <div style={{ 
                    fontSize: '12px', 
                    opacity: 0.6,
                    marginLeft: '10px',
                    flexShrink: 0
                  }}>
                    {song.duration}
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const updatedPlaylist = playlist.filter((_, i) => i !== index);
                      setPlaylist(updatedPlaylist);
                      
                      if (currentSongIndex === index) {
                        // Currently playing song was removed
                        if (updatedPlaylist.length > 0) {
                          const newIndex = Math.min(index, updatedPlaylist.length - 1);
                          setCurrentSong(updatedPlaylist[newIndex]);
                          setCurrentSongIndex(newIndex);
                        } else {
                          setCurrentSong(null);
                          setIsPlaying(false);
                        }
                      } else if (currentSongIndex > index) {
                        // Adjust current song index if removed song was before current
                        setCurrentSongIndex(prev => prev - 1);
                      }
                    }}
                    style={{ 
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'white',
                      fontSize: '12px',
                      cursor: 'pointer',
                      opacity: 0.5,
                      marginLeft: '5px',
                      padding: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%'
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right side - Player and Visualization */}
        <div className="player-container" style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          {/* Album art and visualizer */}
          <div className="album-and-visualizer" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            position: 'relative'
          }}>
            {/* Album art */}
            <div className="album-art" style={{
              width: '80%',
              maxWidth: '320px',
              aspectRatio: '1/1',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              animation: isPlaying ? 'pulse 2s infinite ease-in-out' : 'none',
              zIndex: 1,
              position: 'relative'
            }}>
              {currentSong ? (
                <img 
                  src={currentSong.cover} 
                  alt={currentSong.title}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                    transform: isPlaying ? 'scale(1.02)' : 'scale(1)'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(30, 30, 50, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fas fa-music" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                </div>
              )}
            </div>
            
            {/* Visualizer */}
            {isPlaying && (
              <div style={{
                position: 'absolute',
                bottom: '100px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80%',
                height: '60px',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: '2px',
                padding: '0 20px'
              }}>
                {visualizerData.slice(0, 32).map((value, index) => (
                  <div
                    key={index}
                    style={{
                      width: '6px',
                      height: `${Math.max(3, value * 60)}px`,
                      backgroundColor: index % 2 === 0 ? 'rgba(74, 144, 226, 0.8)' : 'rgba(255, 255, 255, 0.6)',
                      borderRadius: '3px',
                      transition: 'height 0.05s ease',
                      transform: `translateY(${Math.sin(index * 0.2) * 5}px)`
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Song info */}
          <div className="song-info" style={{
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ margin: '0 0 5px 0', fontSize: '1.8rem' }}>
              {currentSong?.title || 'No song selected'}
            </h2>
            <p style={{ margin: 0, opacity: 0.7, fontSize: '1rem' }}>
              {currentSong?.artist || 'Select a song to play'}
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="progress-container" style={{ marginBottom: '15px' }}>
            <div 
              className="progress-bar" 
              onClick={seekTo}
              style={{
                height: '5px',
                borderRadius: '2.5px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              <div 
                className="progress" 
                style={{
                  height: '100%',
                  width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                  borderRadius: '2.5px',
                  backgroundColor: 'rgba(74, 144, 226, 0.8)',
                  position: 'relative',
                  transition: 'width 0.1s linear'
                }}
              >
                <div 
                  className="progress-knob"
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'rgb(74, 144, 226)',
                    position: 'absolute',
                    right: '-6px',
                    top: '-4px',
                    boxShadow: '0 0 5px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.1s ease',
                    transform: 'scale(1)',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              fontSize: '0.8rem',
              opacity: 0.7,
              marginTop: '5px'
            }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
          
          {/* Controls */}
          <div className="controls" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <button 
                onClick={toggleShuffle}
                style={{ 
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: isShuffle ? 'rgb(74, 144, 226)' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '10px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fas fa-random"></i>
              </button>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button 
                onClick={prevSong}
                style={{ 
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '15px',
                  opacity: 0.8,
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fas fa-step-backward"></i>
              </button>
              
              <button 
                onClick={togglePlay}
                style={{ 
                  backgroundColor: 'rgba(74, 144, 226, 0.8)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.2s ease',
                  transform: isPlaying ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                <i className={isPlaying ? 'fas fa-pause' : 'fas fa-play'} style={{ marginLeft: isPlaying ? 0 : '3px' }}></i>
              </button>
              
              <button 
                onClick={nextSong}
                style={{ 
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '15px',
                  opacity: 0.8,
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fas fa-step-forward"></i>
              </button>
            </div>
            
            <div style={{ flex: 1, textAlign: 'right' }}>
              <button 
                onClick={toggleRepeat}
                style={{ 
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: isRepeat ? 'rgb(74, 144, 226)' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '10px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fas fa-redo"></i>
              </button>
            </div>
          </div>
          
          {/* Volume control */}
          <div className="volume-container" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <button 
              onClick={toggleMute}
              style={{ 
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '1rem',
                cursor: 'pointer',
                opacity: 0.7,
                width: '20px'
              }}
            >
              <i className={getVolumeIcon()}></i>
            </button>
            
            <input 
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={changeVolume}
              style={{
                flex: 1,
                height: '4px',
                appearance: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '2px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>
          
          {/* Toggle playlist button - mobile friendly */}
          <button 
            onClick={togglePlaylist}
            style={{ 
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: 'rgba(30, 30, 60, 0.5)',
              border: 'none',
              color: 'white',
              fontSize: '1rem',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            <i className={showPlaylist ? 'fas fa-times' : 'fas fa-list'}></i>
          </button>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgb(74, 144, 226);
          cursor: pointer;
          border: 2px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgb(74, 144, 226);
          cursor: pointer;
          border: 2px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        }
        
        /* For Firefox */
        input[type="range"]::-moz-range-progress {
          background-color: rgba(74, 144, 226, 0.8);
          height: 4px;
          border-radius: 2px;
        }
        
        .playlist-item:hover {
          background-color: rgba(74, 144, 226, 0.2) !important;
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .playlist-container {
            position: absolute;
            z-index: 10;
            height: 100%;
            width: ${showPlaylist ? '80%' : '0'} !important;
            box-shadow: ${showPlaylist ? '0 0 20px rgba(0, 0, 0, 0.5)' : 'none'};
          }
        }
      `}</style>
    </div>
  );
};

export default MusicPlayer; 